import type { CT600Draft } from '../types/ct600Filing'

const DRAFT_KEY_PREFIX = 'ct600-draft-'
const DRAFT_INDEX_KEY = 'ct600-draft-index'
const DRAFT_MAX_AGE_DAYS = 90

function buildDraftKey(companyNumber: string, periodStart: string): string {
  return `${DRAFT_KEY_PREFIX}${companyNumber}-${periodStart}`
}

function getDraftIndex(): string[] {
  try {
    const raw = localStorage.getItem(DRAFT_INDEX_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveDraftIndex(index: string[]): void {
  localStorage.setItem(DRAFT_INDEX_KEY, JSON.stringify(index))
}

export const ct600DraftService = {
  saveDraft(draft: Omit<CT600Draft, 'draftKey'>): void {
    const key = buildDraftKey(draft.companyNumber, draft.setup.accountingPeriodStart)
    const fullDraft: CT600Draft = { ...draft, draftKey: key }

    localStorage.setItem(key, JSON.stringify(fullDraft))

    const index = getDraftIndex()
    if (!index.includes(key)) {
      index.push(key)
      saveDraftIndex(index)
    }
  },

  loadDraft(draftKey: string): CT600Draft | null {
    try {
      const raw = localStorage.getItem(draftKey)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  loadDraftByIdentifiers(companyNumber: string, periodStart: string): CT600Draft | null {
    const key = buildDraftKey(companyNumber, periodStart)
    return this.loadDraft(key)
  },

  listDrafts(): CT600Draft[] {
    const index = getDraftIndex()
    const now = Date.now()
    const maxAge = DRAFT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
    const drafts: CT600Draft[] = []
    const validKeys: string[] = []

    for (const key of index) {
      const draft = this.loadDraft(key)
      if (!draft) continue

      const age = now - new Date(draft.savedAt).getTime()
      if (age > maxAge) {
        localStorage.removeItem(key)
        continue
      }

      drafts.push(draft)
      validKeys.push(key)
    }

    if (validKeys.length !== index.length) {
      saveDraftIndex(validKeys)
    }

    return drafts.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime())
  },

  deleteDraft(draftKey: string): void {
    localStorage.removeItem(draftKey)
    const index = getDraftIndex().filter(k => k !== draftKey)
    saveDraftIndex(index)
  },

  deleteDraftByIdentifiers(companyNumber: string, periodStart: string): void {
    const key = buildDraftKey(companyNumber, periodStart)
    this.deleteDraft(key)
  },
}
